import { HStack, IconButton, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import {
  QueryFilter,
  useCreditFacilityQueryFilters,
  useCreditFacilityQueryParams,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

const FilterPill: React.FC<{
  filterText: string;
  onDeactivateFilter: () => void;
}> = ({ filterText, onDeactivateFilter }) => {
  return (
    <HStack
      border="1px"
      borderRadius="8px"
      borderColor={colors.aurora[400]}
      padding="4px 16px"
      alignItems="center"
      spacing="8px"
    >
      <Text fontWeight="500">{filterText}</Text>
      <IconButton
        aria-label={`deactivate ${filterText} filter`}
        onClick={onDeactivateFilter}
        variant="ghost"
        size="xs"
        icon={<Icon icon="x" size="12px" />}
        _hover={{ bgColor: 'none' }}
      />
    </HStack>
  );
};

const QueryFilterPill: React.FC<{
  queryFilter: QueryFilter;
  upsertQueryFilter: Function;
  refetchReceivables: () => void;
}> = ({ queryFilter, upsertQueryFilter, refetchReceivables }) => {
  const { filterName } = queryFilter;

  const onDeactivateFilter = useCallback(() => {
    upsertQueryFilter({ queryFilter: { ...queryFilter, isActive: false } });
    refetchReceivables();
  }, [queryFilter, refetchReceivables, upsertQueryFilter]);

  return (
    <FilterPill
      filterText={filterName}
      onDeactivateFilter={onDeactivateFilter}
    />
  );
};

export const QueryFiltersPanel: React.FC<{
  organizationId: string;
  refetchReceivables: () => void;
}> = ({ organizationId, refetchReceivables }) => {
  const {
    creditFacilityId: creditFacilityQueryParam,
  } = useCreditFacilityQueryParams();

  const { creditFacilityId: creditFacilityIdUrlParam } = useParams<{
    creditFacilityId: string;
  }>();
  const { queryFilters, upsertQueryFilter } = useCreditFacilityQueryFilters({
    organizationId,
    creditFacilityId: creditFacilityQueryParam ?? creditFacilityIdUrlParam,
  });

  return (
    <HStack spacing="10px" w="100%" m="40px 0">
      {queryFilters.map((queryFilter) =>
        queryFilter.isActive ? (
          <QueryFilterPill
            key={queryFilter.id}
            queryFilter={queryFilter}
            upsertQueryFilter={upsertQueryFilter}
            refetchReceivables={refetchReceivables}
          />
        ) : null,
      )}
    </HStack>
  );
};
